import React, { useState, useEffect } from 'react';
import {
    Plus, FileText, Download, Upload, CheckCircle2,
    X, Calculator, Landmark, ArrowUpRight, Search
} from 'lucide-react';
import api from '../api/axiosInstance';

const ClientFinance = () => {
    const [activeModal, setActiveModal] = useState(null);
    const [invoices, setInvoices] = useState([
        { id: 1, inv_no: 'LI/24/08', project: 'Villa Virar', total: 590000, received: 200000, status: 'Partially Paid' }
    ]);

    const [invForm, setInvForm] = useState({
        project_id: '', invoice_number: '', taxable_amount: 0,
        cgst: 0, sgst: 0, total: 0, invoice_date: ''
    });

    // Auto-calculate GST for Client Invoice
    useEffect(() => {
        const amt = parseFloat(invForm.taxable_amount) || 0;
        const cgst = amt * 0.09;
        const sgst = amt * 0.09;
        setInvForm(prev => ({ ...prev, cgst, sgst, total: amt + cgst + sgst }));
    }, [invForm.taxable_amount]);

    return (
        <div className="space-y-8">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-4xl font-black text-slate-900 tracking-tighter">Client Invoicing</h1>
                    <p className="text-slate-500 font-medium italic">Manage Output GST and Receivables.</p>
                </div>
                <button onClick={() => setActiveModal('new_invoice')} className="bg-indigo-600 text-white px-8 py-4 rounded-2xl font-bold shadow-xl flex items-center gap-2">
                    <Plus size={18} /> Raise New Invoice
                </button>
            </div>

            {/* Invoice Table */}
            <div className="bg-white rounded-[40px] border border-slate-100 shadow-sm overflow-hidden">
                <table className="w-full text-left">
                    <thead className="bg-slate-50/50 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">
                        <tr>
                            <th className="p-6">Invoice #</th>
                            <th className="p-6">Project</th>
                            <th className="p-6">Total Bill (with GST)</th>
                            <th className="p-6">Received</th>
                            <th className="p-6 text-right">Action</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                        {invoices.map(inv => (
                            <tr key={inv.id} className="group hover:bg-slate-50">
                                <td className="p-6 font-bold">{inv.inv_no}</td>
                                <td className="p-6 text-slate-500 font-medium">{inv.project}</td>
                                <td className="p-6 font-black text-slate-900">₹{inv.total.toLocaleString()}</td>
                                <td className="p-6 font-black text-emerald-600">₹{inv.received.toLocaleString()}</td>
                                <td className="p-6 text-right">
                                    <button onClick={() => setActiveModal('receive_payment')} className="px-4 py-2 bg-emerald-50 text-emerald-600 rounded-xl text-[10px] font-black uppercase hover:bg-emerald-600 hover:text-white transition-all">Record Payment</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* MODAL: RAISE NEW INVOICE */}
            {activeModal === 'new_invoice' && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-md">
                    <div className="bg-white w-full max-w-4xl rounded-[48px] shadow-2xl p-12">
                        <div className="flex justify-between items-center mb-10">
                            <h2 className="text-3xl font-black italic">Create Client Invoice</h2>
                            <button onClick={() => setActiveModal(null)} className="p-3 bg-slate-100 rounded-2xl"><X /></button>
                        </div>

                        <form className="space-y-8">
                            <div className="grid grid-cols-2 gap-6">
                                <input type="text" placeholder="Invoice Number (e.g. LI/25/01)" className="p-4 bg-slate-50 rounded-2xl font-bold border-none" onChange={e => setInvForm({ ...invForm, invoice_number: e.target.value })} />
                                <input type="date" className="p-4 bg-slate-50 rounded-2xl font-bold" onChange={e => setInvForm({ ...invForm, invoice_date: e.target.value })} />
                            </div>

                            <div className="bg-slate-900 rounded-[40px] p-10 text-white">
                                <div className="grid grid-cols-3 gap-8 mb-8">
                                    <div>
                                        <label className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-2 block">Taxable Value</label>
                                        <input type="number" className="bg-transparent border-b-2 border-white/20 text-2xl font-black w-full outline-none"
                                            placeholder="0.00" onChange={e => setInvForm({ ...invForm, taxable_amount: e.target.value })} />
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">CGST (9%)</label>
                                        <p className="text-2xl font-black">₹{invForm.cgst.toFixed(2)}</p>
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">SGST (9%)</label>
                                        <p className="text-2xl font-black">₹{invForm.sgst.toFixed(2)}</p>
                                    </div>
                                </div>
                                <div className="pt-8 border-t border-white/10 flex justify-between items-center">
                                    <h3 className="text-4xl font-black">Total: ₹{invForm.total.toLocaleString()}</h3>
                                    <div className="flex gap-4">
                                        <div className="flex flex-col items-center">
                                            <label className="p-4 bg-white/10 rounded-2xl cursor-pointer hover:bg-white/20 transition-all">
                                                <Upload size={20} />
                                                <input type="file" className="hidden" />
                                            </label>
                                            <span className="text-[9px] font-bold mt-1 text-slate-400">Attach Bill Copy</span>
                                        </div>
                                        <button type="submit" className="bg-indigo-500 px-10 py-5 rounded-3xl font-black hover:bg-indigo-400 transition-all">Generate Invoice</button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClientFinance;